import { Injectable, signal, WritableSignal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ApiService } from './api.service';
import { AppService } from '@services/core-services/app.service';
import { TokenService } from '@services/auth-services/token.service';
import { ReplaySubject, Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

interface CacheEntry {
  expiry: number;
  subject: ReplaySubject<any>;
}

@Injectable({ providedIn: 'root' })
export class CachingApiService extends ApiService {
  /**
   * Our TTL-based cache stored as a signal.
   * The signal holds a Map where:
   *   - key: string (URL + serialized query)
   *   - value: CacheEntry
   */
  private cacheSignal: WritableSignal<Map<string, CacheEntry>> = signal(
    new Map<string, CacheEntry>()
  );

  constructor(
    protected override readonly http: HttpClient,
    protected override readonly tokenService: TokenService,
    protected override readonly appService: AppService
  ) {
    super(http, tokenService, appService);
  }

  /**
   * GET with TTL-based caching.
   * Builds a cache key from the fully combined URL (including query parameters).
   */
  public getWithCache<T>(path: string, query?: any, ttl: number = 30000): Observable<T> {
    const params = this.prepareQuery(query);
    const url = this.combine([this.getBaseUrl(), path]);
    const cacheKey = this.buildCacheKey(url, params);

    const cache = this.cacheSignal();
    const existing = cache.get(cacheKey);
    if (existing && Date.now() < existing.expiry) {
      console.log('🎯 Cache hit:', cacheKey);
      return existing.subject.asObservable();
    }

    console.log('⚡ Cache miss, fetching:', cacheKey);

    // Create a new ReplaySubject and add it to the cache via update.
    const subject = new ReplaySubject<T>(1);
    this.cacheSignal.update(map => {
      map.set(cacheKey, { expiry: Date.now() + ttl, subject });
      return map;
    });

    this.http
      .get<T>(url, { params, headers: this.withHeaders() })
      .pipe(
        tap((response: T) => {
          subject.next(response);
          subject.complete();
        }),
        catchError(err => {
          // Remove the entry from the cache on error.
          this.cacheSignal.update(map => {
            map.delete(cacheKey);
            return map;
          });
          subject.error(err);
          return throwError(() => err);
        })
      )
      .subscribe();

    return subject.asObservable();
  }

  /**
   * POST that clears related cache entries on success.
   * (Similarly for PATCH, PUT, and DELETE.)
   */
  public override post<M, T>(path: string, payload: M, query?: any): Observable<T> {
    const url = this.combine([this.getBaseUrl(), path]);
    const params = this.prepareQuery(query);
    const headers = this.withHeaders();

    return this.http
      .post<T>(url, payload, { params, headers })
      .pipe(tap(() => this.clearRelatedCache(path)));
  }

  public override patch<M, T>(path: string, payload: M, query?: any): Observable<T> {
    return super.patch<T, M>(path, payload, query).pipe(tap(() => this.clearRelatedCache(path)));
  }

  public override put<T, M>(path: string, payload: T, query?: any): Observable<M> {
    return super.put<T, M>(path, payload, query).pipe(tap(() => this.clearRelatedCache(path)));
  }

  public override delete<T>(path: string, query?: any): Observable<any> {
    return super.delete<T>(path, query).pipe(tap(() => this.clearRelatedCache(path)));
  }

  /**
   * Build a cache key from the full URL and query parameters.
   */
  private buildCacheKey(url: string, params?: any): string {
    return `${url}?${JSON.stringify(params || {})}`;
  }

  /**
   * Clear any cached GET whose key starts with the collection’s base URL.
   *
   * We derive the “base route” from the provided path. If the last segment
   * appears to be an identifier, we remove it. Then we combine it with the base URL
   * and delete any cache entry whose key starts with that.
   */
  private clearRelatedCache(path: string): void {
    const baseRoute = this.getBaseRoute(path);
    const baseUrl = this.combine([this.getBaseUrl(), baseRoute]);

    console.group('Cache Invalidation');
    console.log('🔍 Current cache keys:', Array.from(this.cacheSignal().keys()));
    console.log('🎯 Looking to invalidate keys starting with:', baseUrl);

    this.cacheSignal.update(map => {
      map.forEach((_, key) => {
        if (key.startsWith(baseUrl)) {
          console.log('❌ Invalidating:', key);
          map.delete(key);
        }
      });
      return map;
    });

    console.log('✅ Remaining cache keys:', Array.from(this.cacheSignal().keys()));
    console.groupEnd();
  }

  /**
   * Given a path like "catalog/products/01JKMW0RZDCN6RFJ9E4W9574N9",
   * return the base route (e.g. "catalog/products") by removing the last segment
   * if it appears to be an ID.
   */
  private getBaseRoute(path: string): string {
    const segments = path.split('/').filter(seg => seg);
    if (segments.length > 1 && this.isProbablyId(segments[segments.length - 1])) {
      segments.pop();
    }
    return segments.join('/');
  }

  /**
   * Simple heuristic to check if a segment matches a ULID.
   */
  private isProbablyId(segment: string): boolean {
    return /^[0-9A-HJKMNP-TV-Z]{26}$/i.test(segment);
  }

  public clearAllCache(): void {
    this.cacheSignal.set(new Map());
  }
}
